import React, { useLayoutEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { IActionMenu } from '@interfaces/components/table';

import styles from './ActionsMenu.module.scss';

interface IActionsMenuParams<T> {
	actions: IActionMenu<T>[];
	closeMenu?: () => void;
	containerClassName?: string;
	isVisibleMenu: boolean;
	minWidth?: number;
	onClick: (actionId: T) => void;
	position?: 'top' | 'bottom';
}

const POSITION_OFFSET = 5;

const ActionsMenu = <T, >({
	actions,
	closeMenu,
	containerClassName,
	isVisibleMenu,
	minWidth,
	onClick,
	position = 'bottom',
}: IActionsMenuParams<T>) => {
	const onClickAction = (action: IActionMenu<T>) => {
		if (action.disabled) {
			return;
		}

		onClick(action.id);
	};

	const containerRef = useRef<HTMLDivElement | null>(null);

	const [containerHeight, setContainerHeight] = useState(0);

	useLayoutEffect(() => {
		if (containerRef.current && isVisibleMenu) {
			setContainerHeight(containerRef.current.offsetHeight + POSITION_OFFSET);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [containerRef.current, isVisibleMenu]);

	const menuPositionStyle = position === 'top'
		? { top: `-${containerHeight}px` }
		: { bottom: `-${containerHeight}px` };

	useOnClickOutside({
		refs: [containerRef],
		handler: () => closeMenu && closeMenu(),
		fireEvent: 'mousedown',
		disableClickOutside: !isVisibleMenu,
	});

	return (
		<div
			ref={containerRef}
			className={cn(containerClassName, styles.menusContainer)}
			style={menuPositionStyle}
		>
			{isVisibleMenu && (
				<div
					className={cn(styles.menu)}
					style={{ minWidth: `${minWidth}px` }}
				>
					{actions.filter((action: IActionMenu<T>) => !action.hidden).map((action: IActionMenu<T>) => {
						return (
							<div
								key={action.text}
								className={cn(styles.menuItem, {
									[styles.disabled]: action.disabled,
								})}
								data-testid='action-item'
								onClick={() => onClickAction(action)}
							>
								{action.text}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default ActionsMenu;
